import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  LESSON_BLOCKS_LIMIT,
  LESSON_INTO_BLOCK_MAX_PAGE_SIZE,
  REQUEST_STALE_TIME_IN_MS as staleTime,
} from '@/constants';
import { useSingleCache } from '@/data/hook/query/v2/useCacheManager';
import useCourseEditing from '@/data/hook/useCourseEditing';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useListService from '@/data/hook/useListService';
import {
  booksQueryKeys,
  coursePathObjectivesQueryKeys,
  coursePathsQueryKeys,
  lessonBlocksQueryKeys,
} from '@/data/services/querykeys';
import { StudioPermissions } from '@/enums/VersioningStatus';
import { Course } from '@/models/Course';
import CoursePath, { TempObjective } from '@/models/CoursePath';
import { TempLesson } from '@/models/Lesson';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';
import { ROLES } from '@/utils/roles';
import useLessons from '../../ManageCourseEditing.hook';

export type OmitCourseAndObjectives = Omit<CoursePath, 'course' | 'objectives'>;

export type CourseStudioFormFields = {
  coursePath?: OmitCourseAndObjectives;
  course?: Course;
  courseLineId?: number;
  lessonBlocks?: TempLessonBlock[];
  directLessons?: TempLesson[];
  objectives?: TempObjective[];
};

export const useCoursePath = () => {
  const { slug = '' } = useParams();

  const { setCourseStatus } = useCourseEditing();
  const { queryFn: coursePathQueryFn, queryKey: coursePathQueryKey } =
    coursePathsQueryKeys.get(slug);

  const { data: coursePath, isInitialLoading: loadingCoursePath } = useQuery({
    queryFn: coursePathQueryFn,
    queryKey: coursePathQueryKey,
    enabled: !!slug,
    staleTime,
  });
  const cacheManager = useSingleCache<CoursePath>(coursePathQueryKey);

  useEffect(() => {
    if (!coursePath) return;
    setCourseStatus(coursePath?.status);
  }, [coursePath, setCourseStatus]);

  return {
    slug,
    coursePath,
    isLoading: loadingCoursePath,
    queryKey: coursePathQueryKey,
    cacheManager,
  };
};

export function useCourseStudio() {
  const { slug, coursePath, isLoading: loadingCoursePath } = useCoursePath();

  const {
    lessonBlocks,
    loadingLessonBlocks,
    loadingLessons,
    lessonBlocksWithLessons,
  } = useLessonsBlockWithLessons(slug);

  const { directLessons } = useLessons(slug);
  const { queryFn: objectivesQueryFn, queryKey: objectivesQueryKey } =
    coursePathObjectivesQueryKeys.list(slug, {
      pageSize: LESSON_BLOCKS_LIMIT,
    });

  const { results: objectives, isInitialLoading: loadingObjectives } =
    useListService({
      queryFn: objectivesQueryFn,
      queryKey: objectivesQueryKey,
      enabled: !!coursePath,
      staleTime,
    });

  const studioPermissions = useMemo<StudioPermissions>(() => {
    if (!coursePath)
      return {
        allowFirstStep: true,
        allowOtherSteps: {
          createEditing: true,
          simpleEditing: true,
          advancedEditing: true,
        },
      };

    const allowFirstStep = true;

    const allowOtherSteps = ROLES.COURSE.STUDIO.allowOtherSteps(
      coursePath.version,
      coursePath.status,
    );

    return { allowFirstStep, allowOtherSteps };
  }, [coursePath]);

  const allLessons = lessonBlocksWithLessons.flatMap(
    block => block.lessons ?? [],
  );

  const { lessonsWithChapters, isLoading: loadingBooks } =
    useAppendChaptersToLessons(allLessons);

  const {
    lessonsWithChapters: directLessonsWithChapters,
    isLoading: loadingDirectLessonsBook,
  } = useAppendChaptersToLessons(directLessons);

  const lessonBlocksWithChapters = lessonBlocksWithLessons.map(block => ({
    ...block,
    lessons: block.lessons.map(
      lesson => lessonsWithChapters.find(l => l.id === lesson.id) || lesson,
    ),
  }));

  const [defaultValues, setDefaultValues] = useState<CourseStudioFormFields>({
    coursePath,
    course: coursePath?.course,
    courseLineId: coursePath?.course?.module?.courseLine?.id,
    lessonBlocks: lessonBlocksWithChapters,
    directLessons: directLessonsWithChapters,
    objectives: objectives.map(objective => new TempObjective(objective)),
  });

  const loading =
    loadingCoursePath ||
    loadingLessonBlocks ||
    loadingObjectives ||
    loadingLessons ||
    loadingBooks ||
    loadingDirectLessonsBook;

  const form = useForm<CourseStudioFormFields>({
    defaultValues,
    mode: 'onChange',
  });

  const [hasReset, setHasReset] = useState(false);

  useEffect(() => {
    const needReset =
      !loading && coursePath && lessonBlocks && objectives && !hasReset;

    if (needReset) {
      form.reset({
        coursePath,
        course: coursePath?.course,
        courseLineId: coursePath?.course?.module?.courseLine?.id,
        lessonBlocks: lessonBlocksWithChapters,
        objectives,
      });
      setDefaultValues({
        coursePath,
        course: coursePath?.course,
        courseLineId: coursePath?.course?.module?.courseLine?.id,
        lessonBlocks: lessonBlocksWithChapters,
        objectives: objectives.map(objective => new TempObjective(objective)),
      });
      setHasReset(true);
    }
  }, [
    coursePath,
    lessonBlocks,
    objectives,
    loading,
    form,
    lessonBlocksWithChapters,
    hasReset,
  ]);
  useEffect(() => {
    if (
      !loading &&
      !defaultValues.directLessons?.length &&
      directLessonsWithChapters.length > 0
    ) {
      setDefaultValues(prev => {
        form.reset({ ...prev, directLessons: directLessonsWithChapters });
        return { ...prev, directLessons: directLessonsWithChapters };
      });
    }
  }, [directLessonsWithChapters, form, loading, defaultValues.directLessons]);

  function resetForm(fields: Partial<CourseStudioFormFields>) {
    form.reset(fields);
    setDefaultValues(fields);
  }

  return {
    coursePath,
    lessonBlocks: lessonBlocksWithChapters,
    objectives,
    defaultValues,
    loading,
    loadingCoursePath,
    loadingLessonBlocks,
    loadingObjectives,
    loadingLessons,
    loadingBooks,
    studioPermissions,
    form,
    resetForm,
  };
}

const useAppendChaptersToLessons = (lessons: TempLesson[]) => {
  const booksQueries = useQueries({
    queries: lessons.map(lesson => ({
      ...booksQueryKeys.get(Number(lesson.book?.id)),
      enabled: ROLES.isValidId(lesson.book?.id),
      staleTime,
    })),
  });

  const lessonsWithChapters = useMemo(
    () =>
      lessons.map((lesson, idx) => ({
        ...lesson,
        chapters: booksQueries[idx]?.data?.chapters ?? [],
      })),
    [lessons, booksQueries],
  );

  const isLoading = booksQueries.some(
    ({ isInitialLoading }) => isInitialLoading,
  );

  return { lessonsWithChapters, isLoading };
};

export const useLessonsBlock = (slugCourseName: string = '') => {
  const { queryFn: lessonBlocksQueryFn, queryKey: lessonBlocksQueryKey } =
    lessonBlocksQueryKeys.list(slugCourseName, {
      pageSize: LESSON_BLOCKS_LIMIT,
    });

  const { results: lessonBlocks, ...rest } = useInfiniteService({
    queryFn: lessonBlocksQueryFn,
    queryKey: lessonBlocksQueryKey,
    enabled: !!slugCourseName,
    staleTime,
  });

  return { lessonBlocks, lessonBlocksQueryKey, ...rest };
};

export const useLessonsBlockWithLessons = (slugCourseName: string = '') => {
  const {
    lessonBlocks,
    lessonBlocksQueryKey,
    isInitialLoading: loadingLessonBlocks,
    ...rest
  } = useLessonsBlock(slugCourseName);
  const queryClient = useQueryClient();

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: lessonBlocksQueryKey,
    });
  };

  const lessonsQueriesKeys = lessonBlocks.map(block => {
    return {
      enabled: !!slugCourseName && !!block.id,
      ...lessonBlocksQueryKeys.listLessons(slugCourseName, block.id, {
        pageSize: LESSON_INTO_BLOCK_MAX_PAGE_SIZE,
        type: ScheduledLessonTypeEnum.NORMAL,
      }),
      staleTime,
    };
  });

  const lessonsQueries = useQueries({
    queries: lessonsQueriesKeys,
  });

  const invalidateBlock = async (blockId: number) => {
    await queryClient.invalidateQueries({
      queryKey: lessonBlocksQueryKeys.listLessons(slugCourseName, blockId, {
        pageSize: LESSON_INTO_BLOCK_MAX_PAGE_SIZE,
      }).queryKey,
    });
  };
  const loadingLessons = lessonsQueries.some(
    ({ isInitialLoading }) => isInitialLoading,
  );
  const lessonBlocksWithLessons = useMemo(
    () =>
      lessonBlocks.map((block, index) => ({
        ...block,
        lessons: lessonsQueries[index]?.data?.results ?? [],
        lessonsQueryKey: lessonsQueriesKeys[index].queryKey,
      })),
    [lessonBlocks, lessonsQueries, lessonsQueriesKeys],
  );

  const lessons = useMemo(() => {
    return lessonBlocksWithLessons.flatMap(block => block.lessons);
  }, [lessonBlocksWithLessons]);

  const isLoading = loadingLessonBlocks || loadingLessons;

  return {
    lessons,
    lessonBlocks,
    lessonBlocksWithLessons,
    lessonBlocksQueryKey,
    loadingLessonBlocks,
    loadingLessons,
    ...rest,
    isLoading,
    invalidate,
    invalidateBlock,
  };
};
